import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { useTranslation } from 'react-i18next';
import { Platform, View } from 'react-native';
import { Button } from 'shared/components/button';

import { Image } from 'shared/components/image';
import { Text } from 'shared/components/text';
import { H1 } from 'shared/components/title';
import { staticImage } from 'shared/library/static-images';
import { useStaticRoute } from 'shared/library/routes/route';
import { ReviewsSummaryCard } from 'shared/components/reviews-summary-card';
import { LangMap } from 'shared/library/routes/define-route';

export function ShopHomeHero({
  className = '',
  headingLevel = 1,
  priority = true,
}: {
  className?: string;
  headingLevel?: number;
  priority?: boolean;
}) {
  const { t } = useTranslation('components');
  const route = useStaticRoute();
  const isNative = Platform.OS !== 'web';
  const puppyBundleCategorySlug: LangMap = {
    da: 'valpe',
    en: 'puppy',
    no: 'valpe',
    sv: 'valp',
  };

  return (
    <View className={`${className} bg-brand-green relative overflow-hidden rounded md:rounded-xl`}>
      <View className={'absolute right-0 top-0 z-10 m-3'}>
        <ReviewsSummaryCard className={'bg-brand-cream-light'} />
      </View>

      <View
        className={
          'absolute left-0 top-0 z-10 size-56 -translate-x-1/4 -translate-y-1/4 -rotate-45 justify-center md:bottom-0 md:left-auto md:right-0 md:top-auto md:size-64 md:translate-x-1/4 md:translate-y-1/4'
        }
      >
        <View className={'w-full bg-yellow-400 py-2 shadow-lg shadow-yellow-950/10'}>
          <Text className={'text-center text-sm font-bold text-yellow-950 md:text-base'}>
            {t('shop.shop_home_hero.great_prices')}
          </Text>
        </View>
      </View>

      <View className={'md:flex-row-reverse'}>
        <View className={'-mb-32 -mt-6 w-full md:my-0 md:-ml-24 md:flex-1'}>
          {!isNative && (
            <View
              className={
                'to-brand-green absolute inset-0 z-10 bg-gradient-to-b from-transparent from-75% md:bg-gradient-to-l'
              }
            />
          )}

          <Image
            src={staticImage('shop-hero')}
            alt={t('shop.shop_home_hero.hero_image_alt')}
            containerClassName={'md:aspect-3/1 pointer-events-none aspect-square w-full md:h-full'}
            imageClassName={'object-cover'}
            priority={priority}
            sizeType={'full'}
          />
        </View>

        <View className={'max-w-xl p-3 md:flex-1 md:justify-center md:p-6 lg:p-12'}>
          <View className={'bg-brand-brown-dark mb-3 self-start rounded-full px-3 py-1'}>
            <Text className={'text-xs font-black uppercase tracking-wide text-white md:text-sm'}>
              {t('shop.shop_home_hero.shop')}
            </Text>
          </View>

          <H1
            title={t('shop.shop_home_hero.hero_title')}
            subtitle={t('shop.shop_home_hero.hero_subtitle')}
            ariaLevel={headingLevel}
            light={true}
            marginBottom={false}
          />

          <View className={'mt-3 gap-1.5 md:mt-6 md:flex-row md:items-center md:gap-3'}>
            <Button
              text={t('shop.shop_home_hero.show_puppy_bundles')}
              icon={falAngleRight}
              color={'brown'}
              href={route('shop.category', { category: puppyBundleCategorySlug })}
            />

            <Button
              text={t('shop.shop_home_hero.browse_categories')}
              icon={falAngleRight}
              color={'white'}
              className={'border-transparent'}
              href={route('shop.categories')}
            />
          </View>
        </View>
      </View>
    </View>
  );
}
